import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

// Format 10 digits as (XXX) XXX-XXXX. Non-US strings are passed through.
const formatUSPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  const d = digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};
const phoneHasEnoughDigits = (v) => String(v || "").replace(/\D/g, "").length >= 10;

export default function Register() {
  const { t } = useTranslation();
  const { register, user, checked } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/account";

  // Prefill from the guest-checkout handoff (BookingPanel sends name/email/phone/dob).
  const [form, setForm] = useState(() => ({
    name: params.get("name") || "",
    email: params.get("email") || "",
    phone: formatUSPhone(params.get("phone") || ""),
    password: "",
  }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const prefilledFromCheckout = !!(params.get("email") && params.get("name") && params.get("phone"));

  useEffect(() => {
    if (checked && user) navigate(user.role === "admin" ? "/admin" : redirect, { replace: true });
  }, [checked, user, navigate, redirect]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhone = (e) => setForm({ ...form, phone: formatUSPhone(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!phoneHasEnoughDigits(form.phone)) {
      setError(t("auth.phoneInvalid"));
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.phone);
      navigate(redirect, { replace: true });
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || t("auth.registerFailed");
      // If the email already has an account, hand off to /login with email pre-filled.
      if (/already exists/i.test(detail) || err.response?.status === 400 && /email/i.test(detail)) {
        if (/already exists/i.test(detail)) {
          const q = new URLSearchParams({ email: form.email });
          if (redirect && redirect !== "/account") q.set("redirect", redirect);
          navigate(`/login?${q.toString()}`, { replace: true });
          return;
        }
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-5 py-28">
      <Seo noindex title="Create Account | Belo Rentals" path="/register" />
      <div className="w-full max-w-md">
        <form onSubmit={submit} data-testid="register-form" className="bg-white rounded-2xl shadow-card p-8">
          <div className="flex items-center gap-3 text-slate-900 mb-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600"><UserPlus size={18} /></span>
            <h1 className="font-heading text-2xl font-semibold">{t("auth.createTitle")}</h1>
          </div>
          <p className="text-slate-500 text-sm mb-7">
            {prefilledFromCheckout ? t("auth.checkoutHandoffSub") : t("auth.createSub")}
          </p>

          {error && <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">{error}</div>}

          <div className="space-y-5">
            <div>
              <span className="label-caps text-slate-400 block mb-2">{t("auth.fullName")}</span>
              <input required value={form.name} onChange={set("name")} data-testid="register-name" className="belo-input" placeholder="John Doe" />
            </div>
            <div>
              <span className="label-caps text-slate-400 block mb-2">{t("auth.email")}</span>
              <input required type="email" value={form.email} onChange={set("email")} data-testid="register-email" className="belo-input" placeholder="you@email.com" />
            </div>
            <div>
              <span className="label-caps text-slate-400 block mb-2">{t("auth.phone")}</span>
              <input
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={setPhone}
                data-testid="register-phone"
                className="belo-input"
                placeholder="(617) 555-1234"
              />
            </div>
            <div>
              <span className="label-caps text-slate-400 block mb-2">{t("auth.password")}</span>
              <input required type="password" minLength={8} value={form.password} onChange={set("password")} data-testid="register-password" className="belo-input" placeholder={t("auth.passwordMin")} autoFocus={prefilledFromCheckout} />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="register-submit" className="mt-7 w-full bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? t("auth.creating") : t("auth.createButton")}
          </button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {t("auth.haveAccount")}{" "}
            <Link to={`/login${redirect !== "/account" ? `?redirect=${redirect}` : ""}`} data-testid="go-login-link" className="text-blue-600 font-semibold hover:underline">{t("auth.signInLink")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
